import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { SyncSelectedQuestion, SyncMappingRow, QuestionMappings } from "../types/mapping";
import { createDefaultQuestionMapping } from "../types/mapping";
import FieldMappingBuilder from "./FieldMappingBuilder";

interface QuestionMappingEditorProps {
  selectedQuestions: SyncSelectedQuestion[];
  questionMappings: QuestionMappings;
  onQuestionMappingsChange: (mappings: QuestionMappings) => void;
}

const QuestionMappingEditor: React.FC<QuestionMappingEditorProps> = ({
  selectedQuestions,
  questionMappings,
  onQuestionMappingsChange,
}) => {
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(
    selectedQuestions[0]?.questionId ?? null
  );
  const [copyMenuAnchor, setCopyMenuAnchor] = useState<null | HTMLElement>(null);

  const activeQuestion = useMemo(
    () => selectedQuestions.find((sq) => sq.questionId === activeQuestionId) ?? null,
    [selectedQuestions, activeQuestionId]
  );

  const activeRows = activeQuestionId ? (questionMappings[activeQuestionId] ?? []) : [];

  const handleRowsChange = (rows: SyncMappingRow[]) => {
    if (!activeQuestionId) return;
    onQuestionMappingsChange({
      ...questionMappings,
      [activeQuestionId]: rows,
    });
  };

  const handleStartMapping = () => {
    if (!activeQuestion) return;
    if (!questionMappings[activeQuestion.questionId]) {
      onQuestionMappingsChange({
        ...questionMappings,
        [activeQuestion.questionId]: createDefaultQuestionMapping(activeQuestion),
      });
    }
  };

  const handleCopyFrom = (sourceQId: string) => {
    if (!activeQuestionId || !sourceQId) return;
    const sourceRows = questionMappings[sourceQId];
    if (!sourceRows) return;
    const copiedRows = sourceRows.map((r) => ({
      ...r,
      id: `${r.id}-copy-${Date.now()}`,
    }));
    onQuestionMappingsChange({
      ...questionMappings,
      [activeQuestionId]: copiedRows,
    });
    setCopyMenuAnchor(null);
  };

  const hasMapping = (qId: string): boolean => {
    const rows = questionMappings[qId];
    return !!rows && rows.some((r) => r.targetField.trim() !== "");
  };

  if (selectedQuestions.length === 0) {
    return (
      <Alert severity="info">
        No hay preguntas seleccionadas. Vuelve al paso anterior para seleccionar las preguntas que
        deseas mapear.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 2, minHeight: 400 }}>
      <Paper variant="outlined" sx={{ width: 280, flexShrink: 0, overflow: "auto" }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2">Preguntas ({selectedQuestions.length})</Typography>
        </Box>
        <List disablePadding dense>
          {selectedQuestions.map((sq) => {
            const mapped = hasMapping(sq.questionId);
            return (
              <ListItemButton
                key={sq.questionId}
                selected={activeQuestionId === sq.questionId}
                onClick={() => setActiveQuestionId(sq.questionId)}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {mapped ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap title={sq.questionText}>
                      {sq.questionText}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {sq.programName} / {sq.itemName}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {activeQuestion ? (
          activeRows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No hay mapeo configurado para esta pregunta.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Button variant="contained" onClick={handleStartMapping}>
                  Crear mapeo
                </Button>
                {Object.keys(questionMappings).some((k) => hasMapping(k)) && (
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
                  >
                    Copiar de otra pregunta
                  </Button>
                )}
              </Box>
              <Menu
                anchorEl={copyMenuAnchor}
                open={!!copyMenuAnchor}
                onClose={() => setCopyMenuAnchor(null)}
              >
                {selectedQuestions
                  .filter((sq) => sq.questionId !== activeQuestionId && hasMapping(sq.questionId))
                  .map((sq) => (
                    <MenuItem key={sq.questionId} onClick={() => handleCopyFrom(sq.questionId)}>
                      <Typography variant="body2" noWrap>
                        {sq.questionText}
                      </Typography>
                    </MenuItem>
                  ))}
              </Menu>
            </Box>
          ) : (
            <FieldMappingBuilder
              rows={activeRows}
              onRowsChange={handleRowsChange}
              currentQuestion={activeQuestion}
            />
          )
        ) : (
          <Alert severity="info">Selecciona una pregunta del panel izquierdo.</Alert>
        )}
      </Box>
    </Box>
  );
};

export default QuestionMappingEditor;
